<template>
  <div class="relative inline-block">
    <!-- Trigger wrapper -->
    <div
      ref="triggerRef"
      :aria-describedby="isVisible ? tooltipId : undefined"
      @mouseenter="onShow('hover')"
      @mouseleave="onHide('hover')"
      @focusin="onShow('focus')"
      @focusout="(e) => onHide('focus', e)"
      @click="onToggle('click')"
    >
      <!-- Slot for button content -->
      <slot name="button" />
    </div>

    <!-- Teleport the tooltip so it avoids clipping containers -->
    <teleport to="body">
      <transition :name="transition">
        <div
          v-if="isVisible && !disabled"
          :id="tooltipId"
          ref="floatingRef"
          :style="floatingStyles"
          class="floating-tooltip px-3 py-1 bg-black dark:bg-gray-800 text-white dark:ring-1 dark:ring-gray-500/50 font-medium rounded text-xs z-50 max-w-96"
          role="tooltip"
        >
          <!-- Slot for tooltip content -->
          <slot name="tooltip">
            {{ content }}
          </slot>
          <!-- Arrow element -->
          <div
            v-if="hasArrow"
            ref="arrowRef"
            :style="arrowStyles"
            :class="arrowBorderClasses"
            class="absolute z-0 w-2 h-2 bg-black dark:bg-gray-800 dark:border-gray-500/50 rotate-45"
          />
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
import { ref, reactive, watch, nextTick, computed, onBeforeUnmount } from 'vue';
import { useFloating, offset, flip, shift, autoUpdate, arrow as floatingArrow } from '@floating-ui/vue';

const props = defineProps({
  content: {
    type: String,
    default: null
  },
  placement: {
    type: String,
    default: "bottom"
  },
  hasArrow: {
    type: Boolean,
    default: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  transition: {
    type: String,
    default: "fade"
  }
})

const tooltipId = `tooltip-${Math.random().toString(36).slice(2)}`;

const triggerRef = ref(null);
const floatingRef = ref(null);
const arrowRef = ref(null);

const isVisible = ref(false);
const activeTriggers = reactive({ hover: false, click: false, focus: false });

// Set up floating UI
const middleware = [
  offset(8),
  flip(),
  shift({ padding: 5 }),
  ...(props.hasArrow ? [floatingArrow({ element: arrowRef })] : []),
];

const { x, y, strategy, update, floatingStyles, whileElementsMounted, middlewareData, placement } =
  useFloating(triggerRef, floatingRef, {
    placement: props.placement,
    middleware,
    strategy: 'fixed',  // use fixed to ‘escape’ overflow containers
    whileElementsMounted: autoUpdate, // ensure tooltip updates on scroll/resize
  });

// Show / hide logic based on triggers
function onShow(triggerType) {
  if (props.disabled) return;
  activeTriggers[triggerType] = true;
  isVisible.value = true;
  nextTick(() => update());
  if (triggerType === 'click') {
    addClickOutsideListener();
  }
}

function onHide(triggerType) {
  activeTriggers[triggerType] = false;

  // If focus is still inside trigger, don't hide
  if (triggerType === "focus" && triggerRef.value?.contains(document.activeElement)) {
    return;
  }

  if (!Object.values(activeTriggers).some(Boolean)) {
    isVisible.value = false;
    removeClickOutsideListener();
  }
}

function onToggle(triggerType) {
  if (props.disabled) return;
  if (activeTriggers[triggerType]) {
    onHide(triggerType);
  } else {
    onShow(triggerType);
  }
}

let clickOutsideHandler = null;

function addClickOutsideListener() {
  if (clickOutsideHandler) return;

  clickOutsideHandler = (event) => {
    const triggerEl = triggerRef.value;
    const floatingEl = floatingRef.value;

    if (
      triggerEl &&
      floatingEl &&
      !triggerEl.contains(event.target) &&
      !floatingEl.contains(event.target)
    ) {
      onHide('click');
    }
  };

  document.addEventListener('mousedown', clickOutsideHandler);
}

function removeClickOutsideListener() {
  if (!clickOutsideHandler) return;
  document.removeEventListener('mousedown', clickOutsideHandler);
  clickOutsideHandler = null;
}

// Clean up on unmount
onBeforeUnmount(() => {
  isVisible.value = false;
  removeClickOutsideListener();
});

// Compute arrow position style
const arrowStyles = computed(() => {
  if (!middlewareData.value.arrow) return {};

  // Default to x/y
  const styles = {
    left: middlewareData.value.arrow.x != null ? `${middlewareData.value.arrow.x}px` : '',
    top: middlewareData.value.arrow.y != null ? `${middlewareData.value.arrow.y}px` : '',
  };

  // Adjust based on placement
  const basePlacement = placement.value.split('-')[0];

  switch (basePlacement) {
    case 'top':
      styles.bottom = '-4px'; // arrow below tooltip
      styles.top = '';
      break;
    case 'bottom':
      styles.top = '-4px'; // arrow above tooltip
      styles.bottom = '';
      break;
    case 'left':
      styles.right = '-4px';
      styles.left = '';
      break;
    case 'right':
      styles.left = '-4px';
      styles.right = '';
      break;
  }

  return styles;
});

const arrowBorderClasses = computed(() => {
  const basePlacement = placement.value.split('-')[0];

  switch (basePlacement) {
    case 'top':
      return 'border-r border-b';

    case 'bottom':
      return 'border-l border-t';

    case 'left':
      return 'border-t border-r';

    case 'right':
      return 'border-b border-l';

    default:
      return '';
  }
});

// Watch for visibility changes to update position
watch(isVisible, (val) => {
  if (val) {
    update();
  }
});
</script>

<style>
.tooltip {
  opacity: 1;
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: none; /* remove transform */
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  transform: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.floating-tooltip > div[ref="arrowRef"] {
  width: 8px;
  height: 8px;
  background: black;
  position: absolute;
  transform: rotate(45deg);
  z-index: 10;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active {
    transition: none !important;
  }
}
</style>